import { INodePositionUpdate } from '../../../../../app/resource/node/node.types';
import { chartNodeParentIdList, chartNodePrimaryParentId } from '../../../../../app/resource/node/node.utils';
import { OrgChartNodeType } from './reactFlowGraph';

/** Flow-space px: when a child's top-left x is within this distance of a parent's x, snap to that parent. */
export const PARENT_X_SNAP_THRESHOLD_PX = 40;

export type DragPositionSnapshot = Map<string, { x: number; y: number }>;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** First chart parent present as a flow node (primary first, then other parent ids). */
function findParentFlowNode(nodes: OrgChartNodeType[], draggedNode: OrgChartNodeType): OrgChartNodeType | undefined {
  const nodeData = draggedNode.data?.nodeData;
  if (!nodeData) {
    return undefined;
  }
  const primary = chartNodePrimaryParentId(nodeData);
  if (primary != null) {
    const byPrimary = nodes.find((n) => n.id === String(primary));
    if (byPrimary) {
      return byPrimary;
    }
  }
  for (const pid of chartNodeParentIdList(nodeData)) {
    const p = nodes.find((n) => n.id === String(pid));
    if (p) {
      return p;
    }
  }
  return undefined;
}

/** Captures node positions at drag start so final changes can be diffed on drag stop. */
export function buildDragPositionSnapshot(nodes: OrgChartNodeType[]): DragPositionSnapshot {
  return new Map(nodes.map((node) => [node.id, { x: node.position.x, y: node.position.y }]));
}

/** Returns only the node positions that changed between the drag snapshot and current flow nodes. */
export function diffNodePositions(snapshot: DragPositionSnapshot, nodes: OrgChartNodeType[]): INodePositionUpdate[] {
  const changedPositions: INodePositionUpdate[] = [];
  for (const node of nodes) {
    const previousPosition = snapshot.get(node.id);
    if (!previousPosition) {
      continue;
    }
    const { x, y } = node.position;
    if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
      continue;
    }
    if (previousPosition.x !== x || previousPosition.y !== y) {
      changedPositions.push({ id: node.id, positionX: x, positionY: y });
    }
  }
  return changedPositions;
}

/** When the dragged node is close to a visible parent's x, snap top-left x to match that parent. */
export function snapDraggedNodeXToPrimaryParentIfClose(
  nodes: OrgChartNodeType[],
  draggedNodeId: string,
  thresholdPx: number = PARENT_X_SNAP_THRESHOLD_PX,
): OrgChartNodeType[] {
  const id = String(draggedNodeId);
  const draggedNode = nodes.find((node) => node.id === id);
  if (!draggedNode?.data?.nodeData) {
    return nodes;
  }
  const parentNode = findParentFlowNode(nodes, draggedNode);
  if (!parentNode) {
    return nodes;
  }
  const childX = draggedNode.position?.x;
  const parentX = parentNode.position?.x;
  if (!isFiniteNumber(childX) || !isFiniteNumber(parentX)) {
    return nodes;
  }
  if (Math.abs(childX - parentX) > thresholdPx) {
    return nodes;
  }
  return nodes.map((node) => (node.id === id ? { ...node, position: { ...node.position, x: parentX } } : node));
}

/** Keeps siblings (same primary parent) aligned to the dragged node Y while dragging. */
export function alignSiblingYForDraggedNode(
  nodes: OrgChartNodeType[],
  draggedNode: OrgChartNodeType,
): OrgChartNodeType[] {
  const draggedParentId = draggedNode.data?.nodeData ? chartNodePrimaryParentId(draggedNode.data.nodeData) : undefined;
  if (draggedParentId == null) {
    return nodes;
  }

  const draggedY = draggedNode.position?.y;
  if (!isFiniteNumber(draggedY)) {
    return nodes;
  }
  return nodes.map((node) => {
    if (node.id === draggedNode.id) {
      return node;
    }
    const nodeParentId = node.data?.nodeData ? chartNodePrimaryParentId(node.data.nodeData) : undefined;
    if (nodeParentId == null || String(nodeParentId) !== String(draggedParentId)) {
      return node;
    }

    return {
      ...node,
      position: {
        ...node.position,
        y: draggedY,
      },
    };
  });
}

export function mergeNodesPreservingMeasured(
  nextNodes: OrgChartNodeType[],
  previousNodes: OrgChartNodeType[],
): OrgChartNodeType[] {
  const previousById = new Map(previousNodes.map((node) => [node.id, node]));

  return nextNodes.map((node) => {
    const previousNode = previousById.get(node.id);
    const withMeasured = previousNode?.measured
      ? { ...node, measured: node.measured ?? previousNode.measured }
      : { ...node };

    if (previousNode?.selected) {
      return { ...withMeasured, selected: true };
    }
    return withMeasured;
  });
}

/** True when this `onNodesChange` batch has more than one user-driven position update (multi-node drag). */
export function isMultiNodeDragPositionBatch(
  changes: { type: string; position?: unknown; dragging?: boolean }[],
): boolean {
  let n = 0;
  for (const change of changes) {
    if (change.type !== 'position' || change.position === undefined || change.dragging === undefined) {
      continue;
    }
    n += 1;
    if (n > 1) {
      return true;
    }
  }
  return false;
}
